import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
import AddCustomerModal from './AddCustomerModal';
import AddDeliveryModal from './AddDeliveryModal';
import AddPaymentModal from './AddPaymentModal';
import AddOrderModal from './AddOrderModal';
import AddExpenseModal from './AddExpenseModal';
import AddFarmPurchaseModal from './AddFarmPurchaseModal';
import AddBrokerPaymentModal from './AddBrokerPaymentModal';
import AddStaffModal from './AddStaffModal';
import SetRateModal from './SetRateModal';
import QuickDeliveryModal from './QuickDeliveryModal';
import './Dashboard.css';

function Dashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [farmPurchases, setFarmPurchases] = useState([]);
  const [brokerPayments, setBrokerPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  
  // Weight Loss State
  const [weightLossSummary, setWeightLossSummary] = useState({
    total_weight_loss: 0,
    total_purchased: 0,
    weight_loss_percentage: 0,
    total_purchases_processed: 0
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Modal states
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showFarmModal, setShowFarmModal] = useState(false);
  const [showBrokerPaymentModal, setShowBrokerPaymentModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showQuickDeliveryModal, setShowQuickDeliveryModal] = useState(false);
  const [selectedOrderForDelivery, setSelectedOrderForDelivery] = useState(null);
  
  const navigate = useNavigate();
  const isCustomer = localStorage.getItem('isCustomer') === 'true';
  const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
  const isAuditor = userRoles.includes('auditor');
  const isSupervisor = userRoles.includes('supervisor');
  const isDeliveryPerson = userRoles.includes('delivery_person');
  const isCashCollector = userRoles.includes('cash_collector');
  const isGM = userRoles.includes('general_manager');

  // ============================================
  // ROLE-BASED ACCESS CONTROL
  // ============================================
  
  const canSeeDashboard = isGM || isSupervisor || isAuditor;
  const canSeeCustomers = isGM || isSupervisor || isAuditor || isDeliveryPerson || isCashCollector;
  const canSeeDeliveries = isGM || isSupervisor || isAuditor || isDeliveryPerson || isCashCollector;
  const canSeeOrders = isGM || isSupervisor || isAuditor || isDeliveryPerson;
  const canSeePayments = isGM || isSupervisor || isAuditor || isCashCollector;
  const canSeeFarm = isGM || isSupervisor || isAuditor;
  const canSeeExpenses = isGM || isSupervisor || isAuditor;
  const canSeeStaff = isGM;

  const canEdit = !isAuditor && !isCustomer;
  const canAddDelivery = isDeliveryPerson || isSupervisor || isGM;
  const canAddPayment = isCashCollector || isGM;
  const canSetRate = isSupervisor || isGM;
  const canAddExpense = isSupervisor || isGM;
  const canAddFarmPurchase = isSupervisor || isGM;
  const canManageStaff = isGM;
  const canAddCustomer = (isGM || isSupervisor) && !isDeliveryPerson && !isCashCollector;
  const canPlaceOrder = (isGM || isSupervisor) && !isCustomer;
  console.log('User Roles:', userRoles);
  console.log('isDeliveryPerson:', isDeliveryPerson);
  console.log('isGM:', isGM);
  console.log('isSupervisor:', isSupervisor);
  const sortedCustomers = [...customers].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'balance') {
      comparison = (a.balance || 0) - (b.balance || 0);
    } else {
      comparison = (a.name || '').localeCompare(b.name || '');
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const sortCustomers = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = () => {
    fetchDashboardData();
    fetchCustomers();
    fetchDeliveries();
    fetchOrders();
    fetchPayments();
    fetchFarmPurchases();
    fetchBrokerPayments();
    fetchExpenses();
    fetchExpenseCategories();
    fetchWeightLossSummary();
    if (canManageStaff) {
      fetchStaffList();
    }
  };

  // ==================== API CALLS ====================

  const fetchDashboardData = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-dashboard`);
      setDashboardData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-customers`);
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDeliveries = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-deliveries`);
      setDeliveries(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchOrders = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const url = isCustomer 
        ? `${API_URL}/get-orders?customerId=${userId}` 
        : `${API_URL}/get-orders`;
      const res = await axios.get(url);
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPayments = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-payments`);
      setPayments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFarmPurchases = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-farm`);
      setFarmPurchases(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBrokerPayments = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-broker-payments`);
      setBrokerPayments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchExpenses = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-expenses`);
      setExpenses(res.data.expenses || []);
      setExpenseCategories(res.data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-expenses`);
      setExpenseCategories(res.data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWeightLossSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-farm`);
      // Calculate weight loss summary from farm purchases
      const purchases = res.data || [];
      const totalWeightLoss = purchases.reduce((sum, p) => sum + (p.weight_loss || 0), 0);
      const totalPurchased = purchases.reduce((sum, p) => sum + (p.quantity_kg || 0), 0);
      const processedCount = purchases.filter(p => p.is_weight_loss_processed).length;
      
      setWeightLossSummary({
        total_weight_loss: totalWeightLoss,
        total_purchased: totalPurchased,
        weight_loss_percentage: totalPurchased > 0 ? (totalWeightLoss / totalPurchased * 100).toFixed(2) : 0,
        total_purchases_processed: processedCount
      });
    } catch (err) {
      console.error(err);
    }
  };

  const processWeightLoss = async (purchaseId) => {
    if (window.confirm('Process weight loss for this purchase?')) {
      try {
        const res = await axios.post(`${API_URL}/process-weight-loss`, { purchaseId });
        if (res.data.warning) {
          alert(`⚠️ WARNING: ${res.data.message}`);
        } else {
          alert(res.data.message);
        }
        fetchFarmPurchases();
        fetchWeightLossSummary();
        fetchDashboardData();
      } catch (err) {
        alert('Error processing weight loss: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleSetRate = (delivery) => {
    setSelectedDelivery(delivery);
    setShowRateModal(true);
  };

  const openChangeOwnPasswordModal = () => {
    const oldPassword = prompt('Enter current password:');
    if (!oldPassword) return;
    const newPassword = prompt('Enter new password:');
    if (!newPassword) return;
    const confirmPassword = prompt('Confirm new password:');
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match!');
      return;
    }
    changeOwnPassword(oldPassword, newPassword);
  };

  const changeOwnPassword = async (oldPassword, newPassword) => {
    try {
      await axios.post(`${API_URL}/change-password`, {
        userId: localStorage.getItem('userId'),
        oldPassword,
        newPassword
      });
      alert('Password changed successfully! Please login again.');
      localStorage.clear();
      navigate('/');
    } catch (err) {
      alert('Error changing password: ' + (err.response?.data?.error || err.message));
    }
  };

  const fetchStaffList = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-staff`);
      setStaffList(res.data.staff || []);
    } catch (err) {
      console.error(err);
    }
  };

  const updateStaffRoles = async (userId, newRoles) => {
    try {
      await axios.post(`${API_URL}/update-staff-roles`, { userId, roles: newRoles });
      alert('Roles updated successfully');
      fetchStaffList();
    } catch (err) {
      alert('Error updating roles: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleStaffStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} this staff member?`)) {
      try {
        await axios.post(`${API_URL}/toggle-staff-status`, { userId, is_active: !currentStatus });
        alert(`Staff ${action}d successfully`);
        fetchStaffList();
      } catch (err) {
        alert('Error updating status: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const openChangePasswordModal = (staff) => {
    const newPassword = prompt(`Enter new password for ${staff.name || staff.username}:`);
    if (newPassword && newPassword.length > 0) {
      changeStaffPassword(staff.id, newPassword);
    }
  };

  const openDeliveryForOrder = (order) => {
  // Pre-fill the delivery modal with this order's customer and order_id
  setSelectedOrderForDelivery(order);
  setShowQuickDeliveryModal(true);
};

  const changeStaffPassword = async (userId, newPassword) => {
    try {
      await axios.post(`${API_URL}/change-staff-password`, { userId, password: newPassword });
      alert('Password changed successfully');
    } catch (err) {
      alert('Error changing password: ' + (err.response?.data?.error || err.message));
    }
  };

  const resetUserSessions = async (userId, userName) => {
    if (window.confirm(`Reset device limit for ${userName}?`)) {
      try {
        await axios.post(`${API_URL}/reset-sessions`, { userId });
        alert(`Sessions cleared for ${userName}.`);
        fetchStaffList();
      } catch (err) {
        alert('Error resetting sessions: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const pendingDeliveries = deliveries.filter(d => !d.is_rate_approved && d.rate === 0);

  return (
    <div className="dashboard">
      <header className="header">
        <div className="logo">
          <img src="/logo192.png" alt="UMER POULTRY" className="header-logo" />
          <div>
            <h2>UMER POULTRY</h2>
            <span>{localStorage.getItem('userName')} ({isCustomer ? 'Customer' : userRoles.join(', ')})</span>
          </div>
        </div>
        <div className="header-buttons">
          {!isCustomer && (
            <button onClick={openChangeOwnPasswordModal} className="change-password-btn">
              🔑 Change Password
            </button>
          )}
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <div className="tabs">
        {canSeeDashboard && <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>Dashboard</button>}
        {canSeeCustomers && <button className={activeTab === 'customers' ? 'active' : ''} onClick={() => setActiveTab('customers')}>Customers</button>}
        {canSeeDeliveries && <button className={activeTab === 'deliveries' ? 'active' : ''} onClick={() => setActiveTab('deliveries')}>Deliveries</button>}
        {canSeeOrders && <button className={activeTab === 'orders' ? 'active' : ''} onClick={() => setActiveTab('orders')}>Orders</button>}
        {canSeePayments && <button className={activeTab === 'payments' ? 'active' : ''} onClick={() => setActiveTab('payments')}>Payments</button>}
        {canSeeFarm && <button className={activeTab === 'farm' ? 'active' : ''} onClick={() => setActiveTab('farm')}>Farm Purchases</button>}
        {canSeeExpenses && <button className={activeTab === 'expenses' ? 'active' : ''} onClick={() => setActiveTab('expenses')}>Expenses</button>}
        {canSeeStaff && <button className={activeTab === 'staff' ? 'active' : ''} onClick={() => setActiveTab('staff')}>Staff</button>}
      </div>

      <div className="tab-content">
        {activeTab === 'dashboard' && dashboardData && canSeeDashboard && (
          <div>
            <h2>Dashboard</h2>
            
            <h3>Today's Summary</h3>
            <div className="dashboard-cards">
              <div className="card"><h3>Today's Sales</h3><p>Rs. {dashboardData.today?.sales?.toLocaleString() || 0}</p></div>
              <div className="card"><h3>Today's Collections</h3><p>Rs. {dashboardData.today?.collections?.toLocaleString() || 0}</p></div>
              <div className="card"><h3>Today's Expenses</h3><p>Rs. {dashboardData.today?.expenses?.toLocaleString() || 0}</p></div>
              <div className="card"><h3>Today's Profit</h3><p className={dashboardData.today?.profit >= 0 ? 'positive' : 'negative'}>Rs. {dashboardData.today?.profit?.toLocaleString() || 0}</p></div>
            </div>
            
            <h3>Stock Summary</h3>
            <div className="dashboard-cards">
              <div className="card"><h3>Total Purchased</h3><p>{dashboardData.stock?.purchased_kg || 0} kg</p></div>
              <div className="card"><h3>Total Sold</h3><p>{dashboardData.stock?.sold_kg || 0} kg</p></div>
              <div className="card"><h3>Available Stock</h3><p className={dashboardData.stock?.available_kg > 0 ? 'positive' : 'negative'}>{dashboardData.stock?.available_kg || 0} kg</p></div>
              <div className="card"><h3>Weight Loss</h3><p>{dashboardData.stock?.weight_loss_kg || 0} kg ({dashboardData.stock?.weight_loss_percent || 0}%)</p></div>
            </div>
            
            <h3>Profit Analysis</h3>
            <div className="dashboard-cards">
              <div className="card"><h3>Total Sales</h3><p>Rs. {dashboardData.overall?.total_sales?.toLocaleString() || 0}</p></div>
              <div className="card"><h3>Total Purchases</h3><p>Rs. {dashboardData.overall?.total_purchases?.toLocaleString() || 0}</p></div>
              <div className="card"><h3>Total Expenses</h3><p>Rs. {dashboardData.overall?.total_expenses?.toLocaleString() || 0}</p></div>
              <div className="card"><h3>Customer Pending</h3><p className="negative">Rs. {dashboardData.overall?.customer_pending?.toLocaleString() || 0}</p></div>
              <div className="card"><h3>Gross Profit</h3><p className={dashboardData.overall?.gross_profit >= 0 ? 'positive' : 'negative'}>Rs. {dashboardData.overall?.gross_profit?.toLocaleString() || 0}</p></div>
              <div className="card"><h3>Net Profit</h3><p className={dashboardData.overall?.net_profit >= 0 ? 'positive' : 'negative'}>Rs. {dashboardData.overall?.net_profit?.toLocaleString() || 0}</p></div>
            </div>
            
            <h3>Pending</h3>
            <div className="dashboard-cards">
              <div className="card"><h3>Customers with Balance</h3><p>{dashboardData.pending?.customer_count || 0}</p></div>
              <div className="card"><h3>Total Pending Amount</h3><p className="negative">Rs. {dashboardData.pending?.total_amount?.toLocaleString() || 0}</p></div>
            </div>
            
            {canSetRate && pendingDeliveries.length > 0 && (
              <div className="pending-rates">
                <h3>Pending Rate Approvals ({pendingDeliveries.length})</h3>
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr><th>Invoice #</th><th>Customer</th><th>Quantity (kg)</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {pendingDeliveries.map(d => (
                        <tr key={d.id}>
                          <td>{d.invoice_no}</td>
                          <td>{d.customer?.name}</td>
                          <td>{d.quantity}</td>
                          <td><button onClick={() => handleSetRate(d)} className="set-rate-btn">Set Rate</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'customers' && canSeeCustomers && (
          <div>
            <h2>Customers</h2>
            <div className="sort-controls">
              <button onClick={() => sortCustomers('balance')} className="sort-btn">Sort by Balance {sortBy === 'balance' && (sortOrder === 'desc' ? '↓' : '↑')}</button>
              <button onClick={() => sortCustomers('name')} className="sort-btn">Sort by Name {sortBy === 'name' && (sortOrder === 'desc' ? '↓' : '↑')}</button>
            </div>
            {canAddCustomer && <button className="add-btn" onClick={() => setShowCustomerModal(true)}>+ Add Customer</button>}
            <div className="table-responsive">
              <table className="data-table">
                <thead><tr><th>Name</th><th>Phone</th><th>Address</th><th>Balance</th></tr></thead>
                <tbody>
                  {sortedCustomers.map(c => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>{c.phone}</td>
                      <td>{c.address}</td>
                      <td className={c.balance > 0 ? 'negative' : 'positive'}>
                        Rs. {c.balance?.toLocaleString() || 0}
                        {c.balance > 0 && <span className="badge badge-danger">Pending</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'deliveries' && canSeeDeliveries && (
          <div>
            <h2>Deliveries</h2>
            {canAddDelivery && <button className="add-btn" onClick={() => setShowDeliveryModal(true)}>+ Record Delivery</button>}
            <div className="table-responsive">
              <table className="data-table">
                <thead><tr><th>Invoice #</th><th>Customer</th><th>Quantity (kg)</th><th>Rate</th><th>Total</th><th>Date</th><th>Status</th>{canSetRate && <th>Action</th>}</tr></thead>
                <tbody>
                  {deliveries.map(d => (
                    <tr key={d.id}>
                      <td>{d.invoice_no}</td>
                      <td>{d.customer?.name}</td>
                      <td>{d.quantity}</td>
                      <td>{d.rate || 'Pending'}</td>
                      <td>Rs. {d.total_amount?.toLocaleString() || 0}</td>
                      <td>{new Date(d.delivery_date).toLocaleDateString()}</td>
                      <td>{d.is_rate_approved ? 'Approved' : 'Rate Pending'}</td>
                      {canSetRate && !d.is_rate_approved && d.rate === 0 && (
                        <td><button onClick={() => handleSetRate(d)} className="set-rate-btn">Set Rate</button></td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && canSeeOrders && (
  <div>
    <h2>Orders</h2>
    {!isCustomer && canPlaceOrder && <button className="add-btn" onClick={() => setShowOrderModal(true)}>+ Place Order</button>}
    {isCustomer && <button className="add-btn" onClick={() => setShowOrderModal(true)}>+ New Order</button>}
    <div className="table-responsive">
      <table className="data-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Ordered (kg)</th>
            <th>Delivered (kg)</th>
            <th>Status</th>
            <th>Order Date</th>
            {(isDeliveryPerson || isGM || isSupervisor) && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {orders.map(o => (
            <tr key={o.id}>
              <td>{o.customer?.name || 'You'}</td>
              <td>{o.ordered_quantity} kg</td>
              <td>{o.delivered_quantity || 0} kg</td>
              <td className={`status-${o.status}`}>{o.status}</td>
              <td>{new Date(o.order_date).toLocaleDateString()}</td>
              {(isDeliveryPerson || isGM || isSupervisor) && o.status === 'pending' && (
                <td>
                  <button 
                    onClick={() => openDeliveryForOrder(o)} 
                    className="deliver-order-btn"
                    title="Make delivery against this order"
                  >
                    🚚 Deliver
                  </button>
                </td>
              )}
              {(isDeliveryPerson || isGM || isSupervisor) && o.status !== 'pending' && (
                <td className="no-action">—</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

        {activeTab === 'payments' && canSeePayments && (
          <div>
            <h2>Payments</h2>
            {canAddPayment && <button className="add-btn" onClick={() => setShowPaymentModal(true)}>+ Record Payment</button>}
            <div className="table-responsive">
              <table className="data-table">
                <thead><tr><th>Customer</th><th>Amount</th><th>Type</th><th>Date</th><th>Collected By</th></tr></thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id}>
                      <td>{p.customer?.name}</td>
                      <td>Rs. {p.amount?.toLocaleString()}</td>
                      <td>{p.payment_type}</td>
                      <td>{new Date(p.payment_date).toLocaleDateString()}</td>
                      <td>{p.collector?.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'farm' && canSeeFarm && (
          <div>
            <h2>Farm Purchases</h2>
            {canAddFarmPurchase && <button className="add-btn" onClick={() => setShowFarmModal(true)}>+ Add Purchase</button>}
            
            <div className="weight-loss-summary">
              <h3>📊 Weight Loss Summary</h3>
              <div className="summary-cards">
                <div className="summary-card"><h4>Total Weight Loss</h4><p className={weightLossSummary.total_weight_loss > 300 ? 'negative' : 'positive'}>{weightLossSummary.total_weight_loss || 0} kg</p></div>
                <div className="summary-card"><h4>Weight Loss %</h4><p>{weightLossSummary.weight_loss_percentage || 0}%</p></div>
                <div className="summary-card"><h4>Processed Purchases</h4><p>{weightLossSummary.total_purchases_processed || 0}</p></div>
              </div>
            </div>
            
            <div className="table-responsive">
              <table className="data-table">
                <thead><tr><th>Broker</th><th>Quantity (kg)</th><th>Rate/kg</th><th>Total</th><th>Weight Loss</th><th>Loss %</th><th>Date</th><th>Status</th>{canSetRate && <th>Action</th>}</tr></thead>
                <tbody>
                  {farmPurchases.map(f => {
                    const lossPercent = f.quantity_kg > 0 ? ((f.weight_loss || 0) / f.quantity_kg * 100).toFixed(2) : 0;
                    return (
                      <tr key={f.id}>
                        <td>{f.broker_name}</td>
                        <td>{f.quantity_kg} kg</td>
                        <td>Rs. {f.farm_rate}</td>
                        <td>Rs. {f.total_amount?.toLocaleString()}</td>
                        <td className={(f.weight_loss || 0) > 300 ? 'negative' : (f.weight_loss || 0) > 0 ? 'warning' : 'positive'}>{(f.weight_loss || 0)} kg</td>
                        <td className={(f.weight_loss || 0) > 300 ? 'negative' : (f.weight_loss || 0) > 0 ? 'warning' : 'positive'}>{lossPercent}%</td>
                        <td>{new Date(f.purchase_date).toLocaleDateString()}</td>
                        <td>{f.is_weight_loss_processed ? <span className="badge-success badge">Processed</span> : <span className="badge-warning badge">Pending</span>}</td>
                        {canSetRate && !f.is_weight_loss_processed && (<td><button onClick={() => processWeightLoss(f.id)} className="process-loss-btn">⚡ Process Loss</button></td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <h3>Payments to Brokers</h3>
            {canAddFarmPurchase && <button className="add-btn" onClick={() => setShowBrokerPaymentModal(true)}>+ Add Broker Payment</button>}
            <div className="table-responsive">
              <table className="data-table">
                <thead><tr><th>Broker</th><th>Amount</th><th>Payment Type</th><th>Date</th><th>Recorded By</th></tr></thead>
                <tbody>
                  {brokerPayments.map(p => (
                    <tr key={p.id}>
                      <td>{p.broker_name}</td>
                      <td>Rs. {p.amount?.toLocaleString()}</td>
                      <td>{p.payment_type}</td>
                      <td>{new Date(p.payment_date).toLocaleDateString()}</td>
                      <td>{p.recorded_by_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'expenses' && canSeeExpenses && (
          <div>
            <h2>Expenses</h2>
            {canAddExpense && <button className="add-btn" onClick={() => setShowExpenseModal(true)}>+ Add Expense</button>}
            <div className="table-responsive">
              <table className="data-table">
                <thead><tr><th>Category</th><th>Amount</th><th>Description</th><th>Date</th></tr></thead>
                <tbody>
                  {expenses.map(e => (
                    <tr key={e.id}>
                      <td>{e.category?.name}</td>
                      <td>Rs. {e.amount?.toLocaleString()}</td>
                      <td>{e.description}</td>
                      <td>{new Date(e.expense_date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'staff' && canSeeStaff && canManageStaff && (
          <div>
            <h2>Staff Management</h2>
            <button className="add-btn" onClick={() => setShowStaffModal(true)}>+ Add Staff</button>
            <div className="table-responsive">
              <table className="data-table">
                <thead><tr><th>Name</th><th>Username</th><th>Phone</th><th>Roles</th><th>Active Sessions</th><th>Device Limit</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {staffList.map(staff => (
                    <tr key={staff.id}>
                      <td>{staff.name || staff.username}</td>
                      <td>{staff.username}</td>
                      <td>{staff.phone || '-'}</td>
                      <td>
                        <select multiple value={staff.roles || []} onChange={(e) => updateStaffRoles(staff.id, Array.from(e.target.selectedOptions, option => option.value))} className="role-select-multiple" size="3">
                          <option value="general_manager">General Manager</option>
                          <option value="supervisor">Supervisor</option>
                          <option value="delivery_person">Delivery Person</option>
                          <option value="cash_collector">Cash Collector</option>
                          <option value="auditor">Auditor</option>
                        </select>
                      </td>
                      <td>{staff.active_sessions || 0} / {staff.device_limit}</td>
                      <td>{staff.device_limit}</td>
                      <td><button onClick={() => toggleStaffStatus(staff.id, staff.is_active)} className={staff.is_active ? 'deactivate-btn' : 'activate-btn'}>{staff.is_active ? 'Deactivate' : 'Activate'}</button></td>
                      <td>
                        <div className="action-buttons">
                          <button onClick={() => openChangePasswordModal(staff)} className="password-btn" title="Change Password">🔑 Change Password</button>
                          <button onClick={() => resetUserSessions(staff.id, staff.name)} className="reset-btn" title="Clear all active sessions">🔓 Reset Device Limit</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

            {/* Modals */}
      {showCustomerModal && <AddCustomerModal onClose={() => setShowCustomerModal(false)} onSuccess={fetchAllData} />}
      {showDeliveryModal && <AddDeliveryModal onClose={() => setShowDeliveryModal(false)} onSuccess={fetchAllData} />}
      {showPaymentModal && <AddPaymentModal onClose={() => setShowPaymentModal(false)} onSuccess={fetchAllData} />}
      {showOrderModal && <AddOrderModal onClose={() => setShowOrderModal(false)} onSuccess={fetchAllData} isCustomer={isCustomer} customerId={localStorage.getItem('userId')} />}
      {showExpenseModal && <AddExpenseModal onClose={() => setShowExpenseModal(false)} onSuccess={fetchAllData} categories={expenseCategories} />}
      {showFarmModal && <AddFarmPurchaseModal onClose={() => setShowFarmModal(false)} onSuccess={fetchAllData} />}
      {showBrokerPaymentModal && <AddBrokerPaymentModal onClose={() => setShowBrokerPaymentModal(false)} onSuccess={fetchAllData} />}
      {showRateModal && selectedDelivery && <SetRateModal onClose={() => setShowRateModal(false)} onSuccess={fetchAllData} delivery={selectedDelivery} />}
      {showStaffModal && <AddStaffModal onClose={() => setShowStaffModal(false)} onSuccess={fetchAllData} />}
      
      {/* Quick Delivery Modal - for delivering against pending orders */}
      {showQuickDeliveryModal && selectedOrderForDelivery && (
        <QuickDeliveryModal 
          onClose={() => {
            setShowQuickDeliveryModal(false);
            setSelectedOrderForDelivery(null);
          }} 
          onSuccess={() => {
            fetchOrders();
            fetchDeliveries();
            fetchDashboardData();
          }} 
          order={selectedOrderForDelivery}
          customerName={selectedOrderForDelivery.customer?.name || 'Customer'}
        />
      )}
    </div>
  );
}

export default Dashboard;